import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";

import { useApiClient } from "@/api/ApiContext";
import { unwrap } from "@/api/client";
import { queryKeys, useProjectOverviewQuery } from "@/api/hooks";
import { usePreviewCandidatesQuery } from "@/api/preview";
import type { FinalSummaryContent } from "@/api/previewFinalSummary";
import { downloadTextFile } from "@/lib/downloadFile";
import {
  buildLabExport,
  buildTranscript,
  candidateForRun,
  inlineTurnText,
  labExportFilename,
  mapLimited,
  turnTextFromDetail,
  type LabExportTurnText,
} from "@/lib/labExport";
import { buildLabRunModel, type LabRunModel } from "@/lib/labRunModel";
import { readLabSelection } from "@/lib/labUrl";
import { Button } from "@/ui/Button";

import { LabFinalSummaryButton } from "./LabFinalSummary";
import { isSimRunTerminal } from "@/api/previewSim";
import { useLabRunData } from "./useLabRunData";

/**
 * Reliv Labs export controls: the run chip with copy, and "Download JSON".
 *
 * The file is built in the browser from what the Lab already read. Patient and
 * Liv text comes from the one body-bearing event read, through the same query
 * keys the conversation uses (so turns already on screen cost nothing); exact
 * prompt bodies are fetched only when the box is ticked. Nothing is stored.
 */

/** Event reads in flight at once while the file is prepared. */
export const EXPORT_CONCURRENCY = 2;

type Phase =
  | { kind: "idle" }
  | { kind: "preparing"; done: number; total: number }
  | { kind: "done"; unreadable: number };

/** The body-bearing event read, shared by the file export and the transcript copy. */
function useEventReader(projectId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return (ref: string, eventRef: string) =>
    queryClient.fetchQuery({
      queryKey: queryKeys.previewRunEvent(projectId, ref, eventRef),
      async queryFn() {
        return unwrap(
          await client.GET(
            "/v1/projects/{projectId}/preview-runs/{previewRunRef}/events/{eventRef}",
            {
              params: {
                path: { projectId, previewRunRef: ref, eventRef },
              },
            },
          ),
        );
      },
      staleTime: Number.POSITIVE_INFINITY,
      gcTime: 0,
      // Liv answers a burst with 429 (503 here); wait it out.
      retry: 6,
      retryDelay: (count) => 1500 * (count + 1),
    });
}

/** Turn text the compact page carries, and the turns whose body still needs a read. */
function splitTurnTexts(model: LabRunModel) {
  const texts = new Map<number, LabExportTurnText>();
  const toRead: { turnNumber: number; eventRef: string }[] = [];
  for (const turn of model.lab.turns) {
    const inline = inlineTurnText(turn);
    if (inline !== null) {
      texts.set(turn.turnNumber, inline);
    } else if (turn.turnEvent !== null) {
      toRead.push({
        turnNumber: turn.turnNumber,
        eventRef: turn.turnEvent.eventRef,
      });
    }
  }
  return { texts, toRead };
}

/** The run's final summary content, or null when there is none to export or it could not be read. */
function useFinalSummaryReader(projectId: string) {
  const client = useApiClient();
  return async (previewRunRef: string): Promise<FinalSummaryContent | null> => {
    try {
      const summary = unwrap(
        await client.GET(
          "/v1/projects/{projectId}/preview-runs/{previewRunRef}/final-summary",
          { params: { path: { projectId, previewRunRef } } },
        ),
      );
      return summary.status === "completed" ? (summary.summary ?? null) : null;
    } catch {
      return null;
    }
  };
}

function useLabExportRunner(projectId: string, model: LabRunModel | null) {
  const read = useEventReader(projectId);
  const readFinalSummary = useFinalSummaryReader(projectId);
  const overview = useProjectOverviewQuery(projectId);
  const candidates = usePreviewCandidatesQuery(projectId);
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const busy = useRef(false);

  async function run(includePrompts: boolean, note: string) {
    if (model === null || busy.current) {
      return;
    }
    busy.current = true;
    const ref = model.previewRunRef;
    const readEvent = (eventRef: string) => read(ref, eventRef);
    // Read beside the turns; a summary that cannot be read only costs its place in the file.
    const finalSummary = readFinalSummary(ref);

    const { texts, toRead } = splitTurnTexts(model);
    const promptRefs = includePrompts
      ? model.prompts.map((row) => row.eventRef)
      : [];
    const total = toRead.length + promptRefs.length;
    let finished = 0;
    let unreadable = 0;
    const tick = () => {
      finished += 1;
      setPhase({ kind: "preparing", done: finished, total });
    };
    setPhase({ kind: "preparing", done: 0, total });

    try {
      const turnResults = await mapLimited(
        toRead,
        EXPORT_CONCURRENCY,
        async (item) => turnTextFromDetail(await readEvent(item.eventRef)),
        tick,
      );
      turnResults.forEach((result, index) => {
        if (result.ok) {
          texts.set(toRead[index]!.turnNumber, result.value);
        } else {
          unreadable += 1;
        }
      });

      let prompts: Map<string, unknown> | null = null;
      if (includePrompts) {
        prompts = new Map();
        const promptResults = await mapLimited(
          promptRefs,
          EXPORT_CONCURRENCY,
          async (eventRef) => (await readEvent(eventRef)).event ?? null,
          tick,
        );
        promptResults.forEach((result, index) => {
          if (result.ok) {
            prompts!.set(promptRefs[index]!, result.value);
          } else {
            unreadable += 1;
          }
        });
      }

      const project = overview.data?.project;
      const now = new Date();
      const summary = await finalSummary;
      const file = buildLabExport({
        now,
        project: {
          id: projectId,
          name: project?.name ?? null,
          tenant: project?.previewTenantId ?? null,
        },
        model,
        candidate: candidateForRun(model, candidates.data?.items ?? []),
        texts,
        prompts,
        note,
        finalSummary: summary,
      });
      downloadTextFile(
        labExportFilename(project?.name ?? null, ref, now),
        JSON.stringify(file, null, 2),
      );
      setPhase({ kind: "done", unreadable });
    } finally {
      busy.current = false;
    }
  }

  return { phase, run };
}

/** "Download JSON" with its "Include full prompts" box and progress. */
export function LabExportPanel({
  projectId,
  model,
  ready,
}: {
  projectId: string;
  model: LabRunModel | null;
  /** The run's events are readable (a run still loading cannot be exported). */
  ready: boolean;
}) {
  const { phase, run } = useLabExportRunner(projectId, model);
  const [includePrompts, setIncludePrompts] = useState(false);
  const [note, setNote] = useState("");
  const preparing = phase.kind === "preparing";
  const canExport = ready && model !== null && !preparing;

  return (
    <div className="space-y-2">
      <Button
        variant="outline"
        size="sm"
        disabled={!canExport}
        onClick={() => void run(includePrompts, note)}
        title={ready ? undefined : "Available once the run has loaded"}
      >
        Download JSON
      </Button>
      <label className="flex items-center gap-1.5 text-xs text-foreground">
        <input
          type="checkbox"
          checked={includePrompts}
          disabled={preparing}
          onChange={(event) => setIncludePrompts(event.target.checked)}
        />
        Include full prompts (large)
      </label>
      <label className="block space-y-1 text-xs text-foreground">
        Bug note
        <textarea
          value={note}
          disabled={preparing}
          rows={3}
          onChange={(event) => setNote(event.target.value)}
          className="block w-full rounded-md border border-border bg-card px-2 py-1 text-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        />
      </label>
      <p role="status" className="text-xs text-muted-foreground">
        {phase.kind === "preparing"
          ? `Preparing ${phase.done}/${phase.total}…`
          : phase.kind === "done" && phase.unreadable > 0
            ? `Downloaded. ${phase.unreadable} ${phase.unreadable === 1 ? "read" : "reads"} failed; those entries are empty in the file.`
            : phase.kind === "done"
              ? "Downloaded."
              : ""}
      </p>
    </div>
  );
}

/** "Copy transcript": the conversation as plain text on the clipboard. */
export function LabTranscriptButton({
  projectId,
  model,
  ready,
}: {
  projectId: string;
  model: LabRunModel | null;
  /** The run's events are readable (a run still loading cannot be copied). */
  ready: boolean;
}) {
  const read = useEventReader(projectId);
  const [phase, setPhase] = useState<"idle" | "preparing" | "copied">("idle");
  const busy = useRef(false);
  const canCopy = ready && model !== null && phase !== "preparing";

  async function copy() {
    if (model === null || busy.current) {
      return;
    }
    busy.current = true;
    setPhase("preparing");
    try {
      const { texts, toRead } = splitTurnTexts(model);
      const results = await mapLimited(
        toRead,
        EXPORT_CONCURRENCY,
        async (item) =>
          turnTextFromDetail(await read(model.previewRunRef, item.eventRef)),
      );
      results.forEach((result, index) => {
        if (result.ok) {
          texts.set(toRead[index]!.turnNumber, result.value);
        }
      });
      await navigator.clipboard.writeText(
        buildTranscript(model.lab.turns, texts),
      );
      setPhase("copied");
      setTimeout(() => setPhase("idle"), 1500);
    } catch {
      setPhase("idle");
    } finally {
      busy.current = false;
    }
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        disabled={!canCopy}
        onClick={() => void copy()}
        title={ready ? undefined : "Available once the run has loaded"}
      >
        {phase === "copied"
          ? "Copied"
          : phase === "preparing"
            ? "Copying…"
            : "Copy transcript"}
      </Button>
      <span role="status" className="sr-only">
        {phase === "copied" ? "Transcript copied" : ""}
      </span>
    </>
  );
}

/** The compact "Run pvs_… ⧉" chip: copies the full run ref. */
export function LabRunChip({ runRef }: { runRef: string }) {
  const [copied, setCopied] = useState(false);
  const short = runRef.length > 16 ? `${runRef.slice(0, 12)}…` : runRef;

  async function copy() {
    try {
      await navigator.clipboard.writeText(runRef);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // A refused clipboard write leaves the ref selectable in the Overview.
      setCopied(false);
    }
  }

  return (
    <span className="inline-flex items-center">
      <button
        type="button"
        onClick={() => void copy()}
        title={runRef}
        aria-label={`Copy Conv. ID ${runRef}`}
        className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-card px-2 text-xs text-foreground hover:bg-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      >
        <span>{copied ? "Copied" : "Copy Conv. ID"}</span>
        <span dir="ltr" className="sr-only font-mono">
          {short}
        </span>
        <span aria-hidden="true">{copied ? "✓" : "⧉"}</span>
      </button>
      <span role="status" className="sr-only">
        {copied ? "Conv. ID copied" : ""}
      </span>
    </span>
  );
}

/** Top bar: the run chip and an "Export" menu, for the run named in the address. */
export function LabTopBarRun({ projectId }: { projectId: string }) {
  const [searchParams] = useSearchParams();
  const runRef = readLabSelection(searchParams).run;
  if (runRef === null) {
    return null;
  }
  return <LabTopBarRunFor key={runRef} projectId={projectId} runRef={runRef} />;
}

function LabTopBarRunFor({
  projectId,
  runRef,
}: {
  projectId: string;
  runRef: string;
}) {
  const data = useLabRunData(projectId, runRef);
  const previewRunRef = data?.previewRunRef ?? null;
  const view = data?.view ?? null;
  const sim = data?.sim ?? null;
  const records = data?.records ?? null;
  const model = useMemo(
    () =>
      previewRunRef === null
        ? null
        : buildLabRunModel({
            previewRunRef,
            view,
            sim,
            records: records ?? [],
          }),
    [previewRunRef, view, sim, records],
  );

  return (
    <div className="flex items-center gap-2">
      <LabRunChip runRef={runRef} />
      <LabTranscriptButton
        projectId={projectId}
        model={model}
        ready={data?.contentState === "ready"}
      />
      <LabFinalSummaryButton
        projectId={projectId}
        runRef={runRef}
        runEnded={
          (data?.sim != null && isSimRunTerminal(data.sim)) ||
          data?.manual?.sessionEnded === true
        }
      />
      <details className="relative">
        <summary className="inline-flex h-8 cursor-pointer list-none items-center rounded-md border border-border bg-card px-3 text-sm font-medium hover:bg-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring">
          Store / Export
        </summary>
        <div className="absolute start-0 z-30 mt-1 w-64 rounded-md border border-border bg-card p-3 shadow-md">
          <LabExportPanel
            projectId={projectId}
            model={model}
            ready={data?.contentState === "ready"}
          />
        </div>
      </details>
    </div>
  );
}
